import React, { useMemo, useState } from 'react';
import {
  Box,
  costFormater,
  List,
  Option,
  OptionKey,
  Text,
} from '@common-fe/common-fe';

import { ICategoryDtoCategoryTypeEnum } from '@/dtos/models/ICategoryDto';
import { LoadMoreButton } from '@/modules/claims/ClaimsProcessor/ClaimsDetails/SpendLimitPerDependent/components/LoadMoreButton';
import { EmptyTransactionsListPlaceholder } from '@/modules/transaction/TransactionsList/EmptyTransactionsListPlaceholder';
import colors from '@/styles/colors';
import theme from '@/styles/theme';

import ServiceCategoryIcon from '../EnrollmentDetailsAccountInfo/EligibleServices/ServiceCategoryIcon';
import { SpendLimitsPeriodHeader } from '../SpendLimitsTable/SpendLimitsPeriodHeader';

import type { CategorySpendLimitRow } from './types';

interface CategorySpendLimitsTableProps {
  categories: CategorySpendLimitRow[];
  isLoading?: boolean;
  showPeriodDropdown?: boolean;
  periodOptions?: Option[];
  selectedPeriod?: OptionKey;
  onPeriodChange?: (value: OptionKey) => void;
  isPeriodLoading?: boolean;
}

const INITIAL_ITEMS_COUNT = 5;

export const CategorySpendLimitsTable: React.FC<CategorySpendLimitsTableProps> = ({
  categories,
  isLoading,
  showPeriodDropdown,
  periodOptions = [],
  selectedPeriod,
  onPeriodChange,
  isPeriodLoading,
}) => {
  const [showAll, setShowAll] = useState(false);
  const hasData = useMemo(() => categories.length > 0, [categories.length]);
  const hasMoreItems = categories.length > INITIAL_ITEMS_COUNT;
  const displayedCategories = useMemo(
    () => (showAll || !hasMoreItems ? categories : categories.slice(0, INITIAL_ITEMS_COUNT)),
    [categories, showAll, hasMoreItems]
  );
  const remainingCount = categories.length - INITIAL_ITEMS_COUNT;

  return (
    <Box
      data-testid="CategorySpendLimitsTable"
      background="module"
      round="container1Round"
      pad="spacing24"
      margin={{ top: 'spacing24' }}
    >
      <SpendLimitsPeriodHeader
        title={`Service category spend limits: ${categories?.length || '0'}`}
        showPeriodDropdown={showPeriodDropdown}
        periodOptions={periodOptions}
        selectedPeriod={selectedPeriod}
        onPeriodChange={onPeriodChange}
        isPeriodLoading={isPeriodLoading}
        testIdPrefix="category-spend-limits"
      />

      <List>
        <List.Header style={{ border: `1px solid ${theme.colors.legend}` }}>
          <List.Header.Item flex={5}>Category</List.Header.Item>
          <List.Header.Item flex={2.5} margin={{ left: 's' }}>
            Spend limit
          </List.Header.Item>
          <List.Header.Item flex={2.5}>Total spent</List.Header.Item>
        </List.Header>

        {isLoading ? (
          <List.Preloader testId="category-spend-limits-preloader" count={1} />
        ) : (
          <>
            <Box style={{ position: 'relative' }}>
              {!hasData && <EmptyTransactionsListPlaceholder title="Category Spend Limits" />}
              {displayedCategories.map((category, index) => {
                const isLastItem = index === displayedCategories.length - 1;
                const shouldShowLoader = isLastItem && showAll && isLoading;

                return (
                  <React.Fragment key={category.id}>
                    <List.Item disableHover data-testid={`category-spend-limit-item-${category.id}`}>
                      <Box style={{ flex: 5 }} direction="row" align="center" gap="spacing8">
                        <ServiceCategoryIcon
                          type={category.categoryType as ICategoryDtoCategoryTypeEnum}
                          size="24px"
                          color={colors.activity[category.categoryType as ICategoryDtoCategoryTypeEnum]}
                        />
                        <Text weight="bold">{category.categoryLabel}</Text>
                      </Box>
                      <List.Item.Text flex={2.5} weight={400} style={{ marginLeft: theme.spacings.s }}>
                        {costFormater(category.categorySpendLimit, true)}
                      </List.Item.Text>
                      <List.Item.Text flex={2.5} weight={400}>
                        {costFormater(category.totalSpent, true)}
                      </List.Item.Text>
                    </List.Item>
                    {shouldShowLoader && (
                      <Box align="center" pad={{ vertical: 'spacing8' }}>
                        <List.Preloader testId="category-spend-limits-last-item-loader" count={1} />
                      </Box>
                    )}
                  </React.Fragment>
                );
              })}
              {hasMoreItems && !showAll && (
                <Box
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '120px',
                    background: theme.colors.fade1,
                  }}
                />
              )}
            </Box>
            {hasMoreItems && (
              <Box pad={{ vertical: 'spacing16' }}>
                <LoadMoreButton
                  isExpanded={showAll}
                  remainingCount={remainingCount}
                  onClick={() => setShowAll(!showAll)}
                  isPending={isLoading}
                />
              </Box>
            )}
          </>
        )}
      </List>
    </Box>
  );
};
